import { useTranslation } from 'react-i18next';

import type { TutorMonthlyHistory } from '../../../types/tutorAi';
import { formatCount } from '../../../utils/format';
import { CompositionBar } from '../../atoms/CompositionBar';
import { SurfaceCard } from '../../molecules/SurfaceCard';

export interface MonthlyHistoryListProps {
  readonly history: readonly TutorMonthlyHistory[];
}

/**
 * Histórico mensal de consumo de um aluno.
 *
 * Lista com barra proporcional em vez de gráfico: a granularidade é de poucos pontos e o
 * que interessa é comparar meses entre si, o que a barra relativa ao mês de maior consumo
 * já entrega — sem um segundo componente de gráfico para manter.
 */
export function MonthlyHistoryList({ history }: MonthlyHistoryListProps) {
  const { t } = useTranslation('tutorAI');

  const peak = history.reduce((max, item) => Math.max(max, item.tokensUsed), 0);

  return (
    <SurfaceCard title={t('student.history.title')}>
      <div className="flex flex-col gap-mini p-xxs">
        {history.length === 0 ? (
          <p className="py-xs text-center text-ctx-content-placeholder ts-paragraph-xs">
            {t('student.history.empty')}
          </p>
        ) : (
          history.map(item => (
            <div key={item.yearMonth} className="flex flex-col gap-quark">
              <div className="flex items-baseline justify-between gap-xxs">
                <span className="text-ctx-content-title ts-heading-xxs">{item.yearMonth}</span>
                <span className="tabular-nums text-ctx-content-base ts-paragraph-xs">
                  {formatCount(item.tokensUsed)}
                </span>
              </div>
              <CompositionBar
                ariaLabel={`${item.yearMonth}: ${formatCount(item.tokensUsed)}`}
                segments={[
                  {
                    key: item.yearMonth,
                    colorClass: 'bg-base-accent-500',
                    percent: peak === 0 ? 0 : (item.tokensUsed / peak) * 100,
                  },
                ]}
              />
            </div>
          ))
        )}
      </div>
    </SurfaceCard>
  );
}
